import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import type { ReactNode } from 'react'

/**
 * Showtime / Daylight, or follow the machine.
 *
 * Legacy is not offered. It preserves the original translucent *macOS* look,
 * and there was never a web equivalent to preserve — shipping it here would be
 * inventing a skin rather than keeping one.
 */
export type SkinPreference = 'system' | 'showtime' | 'daylight'
export type Skin = 'showtime' | 'daylight'

const STORAGE_KEY = 'encore.skin'

interface SkinContextValue {
  /** What the user chose. */
  preference: SkinPreference
  /** What is actually on screen right now. */
  skin: Skin
  setPreference: (next: SkinPreference) => void
}

const SkinContext = createContext<SkinContextValue | null>(null)

function readStoredPreference(): SkinPreference {
  try {
    const stored = localStorage.getItem(STORAGE_KEY)
    if (stored === 'showtime' || stored === 'daylight' || stored === 'system') return stored
  } catch {
    /* private mode */
  }
  return 'system'
}

export function SkinProvider({ children }: { children: ReactNode }) {
  const [preference, setPreferenceState] = useState<SkinPreference>(readStoredPreference)
  const [systemSkin, setSystemSkin] = useState<Skin>(() =>
    typeof window !== 'undefined' && window.matchMedia('(prefers-color-scheme: light)').matches
      ? 'daylight'
      : 'showtime',
  )

  // Track the machine even when pinned, so switching back to System is
  // immediately correct rather than stale until the next OS change.
  useEffect(() => {
    const query = window.matchMedia('(prefers-color-scheme: light)')
    const onChange = (event: MediaQueryListEvent) =>
      setSystemSkin(event.matches ? 'daylight' : 'showtime')
    query.addEventListener('change', onChange)
    return () => query.removeEventListener('change', onChange)
  }, [])

  const skin: Skin = preference === 'system' ? systemSkin : preference

  useEffect(() => {
    document.documentElement.setAttribute('data-skin', skin)
  }, [skin])

  const setPreference = useCallback((next: SkinPreference) => {
    setPreferenceState(next)
    try {
      localStorage.setItem(STORAGE_KEY, next)
    } catch {
      /* private mode — the choice just won't survive a reload */
    }
  }, [])

  const value = useMemo(
    () => ({ preference, skin, setPreference }),
    [preference, skin, setPreference],
  )

  return <SkinContext.Provider value={value}>{children}</SkinContext.Provider>
}

export function useSkin(): SkinContextValue {
  const context = useContext(SkinContext)
  if (!context) throw new Error('useSkin must be used inside <SkinProvider>')
  return context
}
